// Examination.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Tooltip,
  useMediaQuery,
  Fab,
  Slide,
} from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import LinkIcon from "@material-ui/icons/Link";
import { db } from "../firebase";
import { getEmbedUrl } from "../utils/Embed";
import "./Examinations.css";

// Question Item
const QuestionItem = React.memo(({ index, value, onChange, disabled }) => (
  <Paper
    style={{
      marginBottom: 8,
      padding: 8,
      border: value ? "1px solid green" : "2px solid red",
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 56,
    }}
  >
    <Typography style={{ width: 24 }}>{index + 1}.</Typography>
    <RadioGroup
      row
      value={value || ""}
      onChange={onChange}
      style={{ flex: 1, flexWrap: "wrap" }}   // ✅ wrap biar ga berantakan di mobile
    >
      {["A", "B", "C", "D", "E"].map((opt) => (
        <FormControlLabel
          key={opt}
          value={opt}
          control={<Radio color="primary" disabled={disabled} />}
          label={opt}
          style={{ minWidth: 48, margin: "0 4px" }}   // ✅ rapihin opsi
        />
      ))}
    </RadioGroup>
  </Paper>
));

// Skeleton loading
const SkeletonQuestionItem = () => (
  <Paper
    style={{
      marginBottom: 8,
      padding: 8,
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 56,
    }}
  >
    <Box style={{ width: 24, height: 24, backgroundColor: "#ccc", borderRadius: 4 }} />
    <Box style={{ flex: 1, height: 40, backgroundColor: "#ddd", borderRadius: 4 }} />
  </Paper>
);

const Examination = () => {
  const { id } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [time, setTime] = useState(0);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const isMobile = useMediaQuery("(max-width:768px)");
  const questionRefs = useRef([]);
  const [showTooltip, setShowTooltip] = useState(true);
  const [refsReady, setRefsReady] = useState(false);

  // FAB Panel state
  const [openPanel, setOpenPanel] = useState(false);
  const iframeTimeout = useRef(null);


  // Reset state setiap ganti id
  useEffect(() => {
    setData(null);
    setLoadingData(true);
    setAnswers({});
    setStarted(false);
    setPaused(false);
    setTime(0);
    setIframeError(false);
    setIframeLoaded(false);
    setRefsReady(false);

     if (iframeTimeout.current) {
    clearTimeout(iframeTimeout.current);
    iframeTimeout.current = null;
  }
}, [id]);

  // Fetch tryout
  useEffect(() => {
    if (!id) return;
    const fetchTryout = async () => {
      try {
        const doc = await db.collection("tryout").doc(id).get();
        if (doc.exists) {
          const tryout = doc.data();
          setData(tryout);
          setTime(Number(tryout.waktuPengerjaan || 0) * 60);
        } else {
          setIframeError(true);
        }
      } catch (err) {
        console.error(err);
        setIframeError(true);
      } finally {
        setLoadingData(false);
      }
    };
    fetchTryout();
  }, [id]);

  // Timer
  useEffect(() => {
    if (!started || paused || time <= 0) return;
    const interval = setInterval(() => setTime((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(interval);
  }, [started, paused, time]);

  // Auto submit saat waktu habis
  useEffect(() => {
    if (time === 0 && started && !showResult) {
      handleSubmit();
    }
  }, [time, started, showResult]);

  // refs ready
  useEffect(() => {
    if (data && data.jumlahSoal > 0) {
      const timeout = setTimeout(() => setRefsReady(true), 100);
      return () => clearTimeout(timeout);
    }
  }, [data]);

 // Auto-detect iframe error jika tidak load dalam 10,, detik
useEffect(() => {
  if (data && !iframeLoaded && !iframeError) {
    if (iframeTimeout.current) clearTimeout(iframeTimeout.current);
    iframeTimeout.current = setTimeout(() => {
      if (!iframeLoaded) setIframeError(true);
    }, 10000);
    return () => clearTimeout(iframeTimeout.current);
  }
}, [data, iframeLoaded, iframeError]);


  // Auto hide panel setelah 30 detik
  useEffect(() => {
    if (openPanel) {
      const t = setTimeout(() => setOpenPanel(false), 30000);
      return () => clearTimeout(t);
    }
  }, [openPanel]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleAnswerChange = useCallback((q, value) => {
    setAnswers((prev) => ({ ...prev, [q]: value }));
  }, []);

  const handleStartPause = () => {
    if (!started) {
      setStarted(true);
      setPaused(false);
    } else {
      setPaused(!paused);
    }
  };

  const handleSubmit = () => {
    setShowResult(true);
    setShowTooltip(false);
  };


  const handleJumpToQuestionPanel = (index) => {
    if (!refsReady) return;
    const el = questionRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpenPanel(false);
    }
  };

  const totalQuestions = data?.jumlahSoal || 0;
  const jawabanBenar = data?.jawabanBenar || [];
  const answeredCount = Object.keys(answers).length;

  // Loading / error
  if (loadingData) {
    return (
      <Box style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
        <Typography style={{ marginLeft: 8 }}>Memuat tryout...</Typography>
      </Box>
    );
  }

  
  // ✅ Error / data tidak ada
  if (!data || iframeError) {
    return (
      <Box
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <Typography variant="h6" color="error">
          Data tryout tidak ditemukan
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push("/tryout")}
        >
          Kembali
        </Button>
      </Box>
    );
  }

  return (
    <Box style={{ height: "100vh", overflow: "hidden" }}>
      <Grid container style={{ height: "100%" }}>
        {/* LEFT: Soal */}
        <Grid item xs={12} md={6} style={{ borderRight: isMobile ? "none" : "2px solid #ddd", display: "flex", flexDirection: "column", height: isMobile ? "50vh" : "100%" }}>
          {!iframeError && (
            <Box style={{ padding: 8, background: "#000", display: "flex", alignItems: "center", height: 40 }}>
              <Typography variant="h6" style={{ color: "#fff", fontSize: 16 }}>Lembar Soal</Typography>
            </Box>
          )}

          <Box style={{ flex: 1, position: "relative" }}>
          {!iframeLoaded && !iframeError && (
            <Box
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <CircularProgress />
              <Typography>Memuat soal...</Typography>
            </Box>
          )}

          {iframeError ? (
            <Box
              style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: 12,
                background: "#ffe0e0",
              }}
            >
              <Typography variant="h6" color="error">
                Soal gagal dimuat
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIframeError(false);
                  setIframeLoaded(false);
                }}
              >
                Coba Lagi
              </Button>
            </Box>
          ) : (
            <iframe
              src={getEmbedUrl(data.soalUrl)}
              title="Lembar Soal"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: iframeLoaded ? "block" : "none",
              }}
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeError(true)}
            />
          )}
        </Box>

        </Grid>

        {/* RIGHT: Jawaban */}
        <Grid item xs={12} md={6} style={{ display: "flex", flexDirection: "column", height: isMobile ? "50vh" : "100%" }}>
          <Box style={{ padding: 8, background: "#000", borderBottom: "1px solid #ccc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" style={{ color: "#fff", fontSize: 16 }}>Lembar Jawaban</Typography>
            <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Typography variant="h6" style={{ color: time < 60 ? "red" : "lime", fontSize: 16 }}>{formatTime(time)}</Typography>
              <Tooltip
                title={!started ? "Klik untuk memulai ujian" : paused ? "Ujian dijeda, klik Lanjut untuk melanjutkan" : "Klik untuk menjeda ujian"}
                placement="top"
                arrow
                open={showTooltip}
              >
                <span>
                  <Button variant="contained" color="primary" onClick={handleStartPause} size={isMobile ? "small" : "medium"}>
                    {!started ? "Mulai" : paused ? "Lanjut" : "Jeda"}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" color="secondary" onClick={handleSubmit} size={isMobile ? "small" : "medium"}>Selesai</Button>
            </Box>
          </Box>

          <Box style={{ padding: 8 }}>
            <LinearProgress variant="determinate" value={totalQuestions ? (answeredCount / totalQuestions) * 100 : 0} style={{ height: 8, borderRadius: 4 }} color="secondary" />
            <Typography style={{ fontSize: 12, marginTop: 4 }}>{answeredCount}/{totalQuestions} Terjawab</Typography>
          </Box>

          <Box style={{ flex: 1, overflowY: "auto", padding: 8 }}>
            {data ? (
              Array.from({ length: totalQuestions }).map((_, i) => (
                <div key={i} ref={(el) => (questionRefs.current[i] = el)}>
                  <QuestionItem
                    index={i}
                    value={answers[i + 1] || ""}
                    onChange={(e) => handleAnswerChange(i + 1, e.target.value)}
                    disabled={!started || paused}
                  />
                </div>
              ))
            ) : (
              Array.from({ length: 10 }).map((_, i) => <SkeletonQuestionItem key={i} />)
            )}
          </Box>
        </Grid>
      </Grid>

      {/* FAB + Panel lompat soal */}
      <Fab
        color="secondary"
        variant="extended"
        style={{ position: "fixed", bottom: 16, right: 16, zIndex: 2000 }}
        onClick={() => setOpenPanel((prev) => !prev)}
      >
        {openPanel ? "Tutup" : "Panel Soal"}
      </Fab>

      <Slide direction="up" in={openPanel} mountOnEnter unmountOnExit>
        <Box
          position="fixed"
          bottom={80}
          right={16}
          bgcolor="white"
          border="1px solid #ccc"
          borderRadius={8}
          p={2}
          boxShadow={3}
          maxHeight={200}
          overflow="auto"
          zIndex={2000}
        >
          <Grid container spacing={1}>
            {Array.from({ length: totalQuestions }).map((_, i) => (
              <Grid item key={i}>
                <Button
                  size="small"
                  variant="outlined"
                  style={{
                    width: 36,
                    height: 36,
                    minWidth: 0,
                    backgroundColor: answers[i + 1] ? "green" : "red",
                    color: "#fff",
                    padding: 0,
                  }}
                  onClick={() => handleJumpToQuestionPanel(i)}
                >
                  {i + 1}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Slide>

      {/* Modal hasil */}
      <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="md" fullWidth>
        <DialogTitle>Hasil Ujian</DialogTitle>
        <DialogContent dividers style={{ maxHeight: 400, overflowY: "auto" }}>
          {data && (
            <>
              <Typography variant="h6">
                Jumlah Benar: {jawabanBenar.filter((_, i) => answers[i + 1] === jawabanBenar[i]).length}
              </Typography>
              <Typography variant="h6">
                Jumlah Salah: {jawabanBenar.filter((_, i) => answers[i + 1] && answers[i + 1] !== jawabanBenar[i]).length}
              </Typography>
              <Typography variant="h6" style={{ marginBottom: 12 }}>
                Belum Dijawab: {jawabanBenar.filter((_, i) => !answers[i + 1]).length}
              </Typography>

              <Grid container spacing={1} style={{ marginBottom: 8, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>
                <Grid item xs={1}><Typography variant="subtitle2">No</Typography></Grid>
                <Grid item xs={3}><Typography variant="subtitle2">Jawaban Anda</Typography></Grid>
                <Grid item xs={3}><Typography variant="subtitle2">Jawaban Benar</Typography></Grid>
                <Grid item xs={5}><Typography variant="subtitle2">Keterangan</Typography></Grid>
              </Grid>

              {jawabanBenar.map((jb, i) => {
                const ans = answers[i + 1];
                const benar = ans === jb;
                return (
                  <Grid container spacing={1} key={i} style={{ marginBottom: 4 }}>
                    <Grid item xs={1}>
                      <Typography variant="body2">{i + 1}.</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" style={{ color: !ans ? "gray" : benar ? "green" : "red" }}>
                        {ans || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">{jb}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="body2" style={{ color: benar ? "green" : ans ? "red" : "gray" }}>
                        {benar ? "Benar" : ans ? "Salah" : "Kosong"}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })}

              {data?.jawabanUrl && (
                <Box style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 4 }}>
                  <Link
                    href={data.jawabanUrl}
                    target="_blank"
                    rel="noopener"
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <LinkIcon fontSize="small" />
                    Lihat Jawaban Lengkap Disini
                  </Link>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => history.push("/tryout")} color="primary" variant="contained">
            Selesai
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Examination;
